import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  KeyRound,
  User,
  Lock,
  Shield,
  Save,
  ArrowLeft,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

const ChangeUserPassword = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [newUserData, setNewUserData] = useState({});
  const [creatingUser, setCreatingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setNewUserData({ ...newUserData, [e.target.name]: e.target.value });
  };

  const fetchUser = async () => {
    try {
      const data = await requestWithAuth(`/users/accounts/${user_id}/`, "GET");
      setNewUserData(data);
    } catch (error) {
      toast.error("Failed to get user details. Please try again later.");
    }
  };

  const updateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    const body = JSON.stringify(newUserData);
    try {
      const response = await requestWithAuth(
        `/users/accounts/${user_id}/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      setCreatingUser(false);
      toast.success("Password changed Successfully");
      navigate("/administration/users");
    } catch (error) {
      toast.error("Password change failed. Please try again later.");
      setCreatingUser(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);



  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => (navigate("/administration/users"))}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Users</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Administration</span>
                <span>/</span>
                <span>Users</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Change Password
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <KeyRound size={32} className="text-purple-600" />
                Change User Password
              </h1>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Shield className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Security Settings
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Update the user's password securely
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Security Notice */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <AlertCircle size={18} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-purple-900">
                        Security Notice
                      </p>
                      <p className="text-xs text-purple-700 mt-1">
                        You are about to change the password for this user. Make
                        sure to use a strong password and inform the user of the
                        change.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Username Display */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <User size={16} className="text-gray-500" />
                    Username / Login ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newUserData?.username || ""}
                      disabled
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-gray-100 cursor-not-allowed pr-10"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <User size={18} className="text-gray-400" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Changing password for:{" "}
                    <span className="font-semibold">
                      {newUserData?.first_name} {newUserData?.last_name}
                    </span>
                  </p>
                </div>

                {/* New Password */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Lock size={16} className="text-gray-500" />
                    New Password
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="new_password"
                      required
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

              </div>

              {/* Card Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      (navigate("/administration/users"))
                    }
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  {creatingUser ? (
                    <button
                      type="button"
                      disabled
                      className="bg-purple-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={updateUser}
                      disabled={!newUserData?.new_password}
                      className={`rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2 ${newUserData?.new_password
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      <Save size={18} />
                      Change Password
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security Tips */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Shield size={18} className="text-purple-600" />
              Security Best Practices
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <KeyRound size={16} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Strong Passwords
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Use unique, complex passwords for each account
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <AlertCircle size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Notify Users
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Always inform users when their password is changed
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Lock size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Regular Updates
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Encourage users to change passwords periodically
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Shield size={16} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Secure Storage
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Never share passwords via insecure channels
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeUserPassword;
